import { Injectable } from "@angular/core";

import { MENTION_RESULT_LIMIT, MentionCandidate, mentionDisplayName } from "@mypxplat/xplat/core";

import { WebCommunityService } from "./community.service";

/**
 * Remote search debounce. Every keystroke used to fire an unthrottled Firestore
 * round trip, and whichever one happened to land last won -- so the list
 * flickered and regularly showed results for a query the user had moved past.
 */
const REMOTE_DEBOUNCE_MS = 150;

export type MentionSource = "community_profiles" | "collaborators";

export interface MentionSearchOptions {
  /**
   * Where candidates come from. Community searches Firestore; collaborators
   * filters an in-memory list the host component already has.
   */
  source: MentionSource;
  query: string;
  /** Required for the "collaborators" source. */
  dataSource?: any[];
  limit?: number;
}

/**
 * The single entry point for "who can be mentioned here".
 *
 * Previously each surface did its own thing: community hit Firestore with a
 * prefix match, workspace and file chat filtered an array with `indexOf` and no
 * cap at all, and mobile had a third copy that quietly omitted the Studio One
 * Experts entry. This normalises all of it -- same shape, same cap, same
 * ranking -- so the picker never has to care which surface it is on.
 */
@Injectable({ providedIn: "root" })
export class MentionSourceService {
  /**
   * Monotonic token. Any response whose token is no longer current belongs to a
   * query the user has already typed past, and is dropped.
   */
  private _sequence = 0;
  private _pendingTimeout: any;
  private _pendingResolve: ((value: MentionCandidate[] | null) => void) | undefined;

  constructor(private communityService: WebCommunityService) {}

  /**
   * Resolves to the candidates to show, or to `null` when this search was
   * superseded by a newer one and the caller should simply do nothing.
   */
  search(options: MentionSearchOptions): Promise<MentionCandidate[] | null> {
    const token = ++this._sequence;
    const limit = options.limit || MENTION_RESULT_LIMIT;
    const query = (options.query || "").trim();

    // Anything already queued or in flight is now stale. Settle it rather than
    // just dropping the timer, or its promise would hang forever.
    this._abandonPending();

    if (!query) return Promise.resolve([]);

    // The collaborator list is already in memory, so there is nothing to
    // debounce or race -- answer synchronously.
    if (options.source === "collaborators") {
      return Promise.resolve(this._filterLocal(options.dataSource, query, limit));
    }

    return new Promise((resolve) => {
      this._pendingResolve = resolve;

      this._pendingTimeout = setTimeout(() => {
        this._pendingTimeout = undefined;
        if (token !== this._sequence) return this._settle(resolve, null);

        this.communityService
          .findMembers(false, query.toLowerCase(), false, limit, true)
          .then((results) => {
            this._settle(resolve, token !== this._sequence ? null : this._normalize(results, limit));
          })
          .catch(() => {
            this._settle(resolve, token !== this._sequence ? null : []);
          });
      }, REMOTE_DEBOUNCE_MS);
    });
  }

  /**
   * Invalidates anything in flight. Call when the picker closes so a late
   * response cannot pop it back open.
   */
  cancel() {
    this._sequence++;
    this._abandonPending();
  }

  /** Resolves the outstanding search with null, meaning "ignore me". */
  private _abandonPending() {
    if (this._pendingTimeout) {
      clearTimeout(this._pendingTimeout);
      this._pendingTimeout = undefined;
    }
    if (this._pendingResolve) {
      const resolve = this._pendingResolve;
      this._pendingResolve = undefined;
      resolve(null);
    }
  }

  private _settle(resolve: (value: MentionCandidate[] | null) => void, value: MentionCandidate[] | null) {
    if (this._pendingResolve === resolve) this._pendingResolve = undefined;
    resolve(value);
  }

  private _filterLocal(dataSource: any[], query: string, limit: number): MentionCandidate[] {
    if (!dataSource || !dataSource.length) return [];
    const needle = query.toLowerCase();

    const matches = dataSource.filter((item) => {
      const name = mentionDisplayName(item).toLowerCase();
      return !!name && name.indexOf(needle) > -1;
    });

    // Someone whose name starts with the query is a better answer than someone
    // who merely contains it, and only five rows are shown.
    matches.sort((a, b) => {
      const aStarts = mentionDisplayName(a).toLowerCase().indexOf(needle) === 0;
      const bStarts = mentionDisplayName(b).toLowerCase().indexOf(needle) === 0;
      if (aStarts === bStarts) return 0;
      return aStarts ? -1 : 1;
    });

    return this._normalize(matches, limit);
  }

  /**
   * Guarantees `id` and a display `name` without throwing the rest of the
   * profile away.
   *
   * The selected candidate ends up on the persisted mention record as `user`,
   * and that used to be the full Firestore profile (firstName, lastName, email,
   * ...). Narrowing it here would silently change what is written to Firestore
   * and drop fields the mobile composer reads off its own mention records.
   */
  private _normalize(results: any[], limit: number): MentionCandidate[] {
    if (!results || !results.length) return [];

    return results
      .filter((item) => item && item.id && mentionDisplayName(item))
      .slice(0, limit)
      .map((item, index) =>
        Object.assign({}, item, {
          name: mentionDisplayName(item),
          // The picker highlights the first row so Enter always has a target.
          selected: index === 0,
        })
      );
  }
}
